// =============================================================================
// features/conversations/assignment.ts — O que o painel de atendente mostra
// (F43-S27).
//
// Lógica PURA, separada do componente, porque foi exatamente aqui que o
// produto quebrou em produção: o `ContactPanel` decidia sozinho
// (`canAssign = canManage && activeUsers.length > 0`) usando uma lista que
// vinha de `/api/admin/users` — rota de administração, que exige
// `users:manage`. A vendedora tinha `livechat:conversation:manage`, a lista
// voltava vazia em silêncio, e a ação de pegar a própria conversa
// simplesmente não existia na tela.
//
// Agora quem decide é o backend (`access` do detalhe da conversa) e o seletor
// de pessoas virou BOTÃO: "atribuir a mim" / "liberar para o inbox". Sem
// lista, sem dependência de rota de administração.
// =============================================================================

import type { ConversationAccess, ConversationReadOnlyReason } from './types';

/** Texto único de somente leitura — mesma frase que a API usa ao recusar. */
export const READ_ONLY_NOTICE: Record<ConversationReadOnlyReason, string> = {
  assigned_to_other_out_of_city:
    'Esta conversa já tem outro atendente e é de uma cidade fora do seu escopo — você pode acompanhá-la, mas não escrever nela.',
};

/** Rótulo exibido quando a conversa tem dono mas o nome não é resolvível. */
export const UNKNOWN_ASSIGNEE_LABEL = 'Outro atendente';

/** Rótulo de "a conversa é sua" — não depende de nenhuma lista de usuários. */
export const SELF_ASSIGNEE_LABEL = 'Você';

export interface AssignableUser {
  readonly id: string;
  readonly fullName: string;
  readonly status: string;
}

export interface AssignmentPanelInput {
  /** `undefined` enquanto o detalhe carrega — nenhuma ação é oferecida antes da resposta. */
  readonly access: ConversationAccess | undefined;
  readonly assignedUserId: string | null;
  /** `users.id` do usuário logado. */
  readonly currentUserId: string | null;
  /**
   * Lista de usuários para o repasse da GESTÃO. Só chega preenchida para quem
   * tem `users:manage` — e o agente não depende dela para nada.
   */
  readonly users: readonly AssignableUser[];
}

export interface AssignmentPanelState {
  /** Botão "Atribuir a mim". */
  readonly showAssignToMe: boolean;
  /** Botão "Liberar para o inbox" (desatribuir). */
  readonly showRelease: boolean;
  /** Seletor de pessoas — só para papéis de gestão, e só se a lista existir. */
  readonly showTransferSelect: boolean;
  /** Nome a exibir no cabeçalho do painel; `null` = "Sem atendente atribuído". */
  readonly assignedLabel: string | null;
  /** Aviso de somente leitura; `null` quando a conversa é editável. */
  readonly readOnlyNotice: string | null;
  /** Usuários ativos para o seletor de repasse. */
  readonly transferOptions: readonly AssignableUser[];
}

/**
 * Resolve o estado do painel de atendente a partir do que a API autorizou.
 *
 * Regras conscientes:
 *  - Enquanto `access` não chegou, nada é oferecido — nunca oferecer uma ação
 *    que o servidor vai recusar (e nunca esconder por padrão uma que ele
 *    permite: a ausência aqui é transitória, não uma decisão).
 *  - O nome do atendente NÃO depende da lista de administração: conversa
 *    minha exibe "Você". Antes, uma conversa atribuída à própria vendedora
 *    aparecia como "Sem atendente atribuído", porque o nome era procurado numa
 *    lista que para ela vinha vazia.
 */
export function resolveAssignmentPanel(input: AssignmentPanelInput): AssignmentPanelState {
  const { access, assignedUserId, currentUserId, users } = input;
  const activeUsers = users.filter((u) => u.status === 'active');

  const assignedLabel = resolveAssignedLabel(assignedUserId, currentUserId, activeUsers);
  const readOnlyNotice =
    access !== undefined && access.readOnlyReason !== null
      ? READ_ONLY_NOTICE[access.readOnlyReason]
      : null;

  if (access === undefined) {
    return {
      showAssignToMe: false,
      showRelease: false,
      showTransferSelect: false,
      assignedLabel,
      readOnlyNotice,
      transferOptions: [],
    };
  }

  return {
    // `currentUserId === null` só acontece com sessão corrompida — sem id não
    // há a quem atribuir, e o botão mentiria.
    showAssignToMe: access.canAssignSelf && !access.assignedToMe && currentUserId !== null,
    showRelease: access.canAssignSelf && assignedUserId !== null,
    showTransferSelect: access.canAssignOthers && activeUsers.length > 0,
    assignedLabel,
    readOnlyNotice,
    transferOptions: access.canAssignOthers ? activeUsers : [],
  };
}

function resolveAssignedLabel(
  assignedUserId: string | null,
  currentUserId: string | null,
  activeUsers: readonly AssignableUser[],
): string | null {
  if (assignedUserId === null) return null;
  if (currentUserId !== null && assignedUserId === currentUserId) return SELF_ASSIGNEE_LABEL;
  return activeUsers.find((u) => u.id === assignedUserId)?.fullName ?? UNKNOWN_ASSIGNEE_LABEL;
}
